/**
 * API client for the admin-dashboard server.
 *
 * Handles the base URL, Cognito auth headers (refreshing the session once on a 401),
 * JSON bodies and parsing, and turns failures into `ApiError`s.
 */
import { Auth } from "aws-amplify";
import { environment } from "../config/environment";
import { handleTokenExpiration } from "../utils/Auth";

const API_BASE_URL = environment.apiUrl;

export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

const sessionHeaders = (session) => ({
  token: session.getAccessToken().getJwtToken(),
  "id-token": session.getIdToken().getJwtToken(),
});

const getAuthHeaders = async ({ forceRefresh = false } = {}) => {
  if (forceRefresh) await Auth.currentAuthenticatedUser({ bypassCache: true });
  return sessionHeaders(await Auth.currentSession());
};

const buildHeaders = async ({ headers, auth, forceRefresh }) => ({
  "Content-Type": "application/json",
  ...headers,
  ...(auth ? await getAuthHeaders({ forceRefresh }) : {}),
});

const parseBody = async (response) => {
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) return response.json();
  return response.text();
};

const expireSession = () => {
  handleTokenExpiration();
  return new ApiError("Session expired", 401);
};

/**
 * @param {string} endpoint - Path under the API base URL, e.g. "/mc/runs"
 * @param {Object} options - Fetch options
 * @param {string} options.method - HTTP method (default: "GET")
 * @param {Object} options.body - Request body (JSON stringified)
 * @param {Object} options.headers - Additional headers
 * @param {boolean} options.auth - Whether to send the Cognito tokens (default: true)
 * @returns {Promise<any>} Parsed JSON (or text) response
 */
const request = async (endpoint, { method = "GET", body, auth = true, headers: customHeaders, ...rest }, readOk) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const send = async (forceRefresh = false) =>
    fetch(url, {
      method,
      headers: await buildHeaders({ headers: customHeaders, auth, forceRefresh }),
      ...(body !== undefined && { body: JSON.stringify(body) }),
      ...rest,
    });

  try {
    let response;
    try {
      response = await send();
      if (auth && response.status === 401) response = await send(true);
    } catch (error) {
      if (auth && /no current user|not authenticated|refresh token/i.test(String(error?.message || error))) {
        throw expireSession();
      }
      throw error;
    }

    if (auth && response.status === 401) throw expireSession();

    if (!response.ok) {
      const data = await parseBody(response);
      const message =
        data?.error ||
        data?.message ||
        (typeof data === "string" && data ? data : `Request failed with status ${response.status}`);
      throw new ApiError(message, response.status, data);
    }

    return await readOk(response);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error?.message || "An unexpected error occurred", 0, null);
  }
};

export const apiClient = (endpoint, options = {}) => request(endpoint, options, parseBody);

const attachmentName = (response) =>
  /filename="?([^";]+)"?/i.exec(response.headers.get("content-disposition") || "")?.[1];

/**
 * Fetches a file with the session's auth headers and returns it as a Blob, with the
 * filename from Content-Disposition when the server sends (and exposes) one.
 */
export const apiBlob = (endpoint, options = {}) =>
  request(endpoint, options, async (response) => ({
    blob: await response.blob(),
    filename: attachmentName(response),
  }));

export const api = {
  get: (endpoint, options = {}) => apiClient(endpoint, { ...options, method: "GET" }),

  post: (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: "POST", body }),

  put: (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: "PUT", body }),

  patch: (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: "PATCH", body }),

  delete: (endpoint, options = {}) => apiClient(endpoint, { ...options, method: "DELETE" }),

  blob: (endpoint, options = {}) => apiBlob(endpoint, { ...options, method: "GET" }),
};

export default api;
